import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  BadgeCheck,
  Search,
  Plus,
  Save,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
  Hash,
  Type,
  Building2,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const DesignationMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";

  const [loading, setLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  // dropdown departments
  const [departments, setDepartments] = useState([]);

  // form
  const [showForm, setShowForm] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [oldDesigName, setOldDesigName] = useState("");
  const [desigCode, setDesigCode] = useState("");
  const [desigName, setDesigName] = useState("");
  const [deptId, setDeptId] = useState("Select");
  const [isActive, setIsActive] = useState(false);

  // list
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-2", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const resetForm = () => {
    setEditingId(null);
    setOldDesigName("");
    setDesigCode("");
    setDesigName("");
    setDeptId("Select");
    setIsActive(false);
  };

  const fetchDepartments = async () => {
    if (!String(finYearId).trim()) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/designation-master/departments`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success) {
        setDepartments(res.data.rows || []);
      } else {
        toast.error(res?.data?.message || "Failed to load departments.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/designation-master`, {
        params: {
          finYearId: Number(finYearId),
          page: p,
          pageSize,
          search: search.trim(),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);

        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Failed to load designations.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  // ✅ Clear filter and reload full list
  const handleClearFilters = () => {
    setSearch("");
    setPage(1);
    fetchList(1);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
    toast.info("Form ready for new designation");
  };

  const handleEdit = async (id) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/designation-master/${id}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res?.data?.row) {
        const r = res.data.row;

        setEditingId(r.id);
        setOldDesigName(r.desigName || "");
        setDesigCode(r.desigCode || "");
        setDesigName(r.desigName || "");
        setDeptId(String(r.deptId ?? "Select"));
        setIsActive(Number(r.isActive) === 1);

        setShowForm(true);
        toast.info("Loaded for editing");
      } else {
        toast.error(res?.data?.message || "Record not found.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const cleaned = desigName.replace(/\s/g, "");
    if (!cleaned) {
      toast.warn("Please Enter Designation Name");
      return false;
    }
    if (!deptId || deptId === "Select") {
      toast.warn("Please Select Department");
      return false;
    }
    return true;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        desigCode: desigCode.trim(),
        desigName: desigName.trim(),
        deptId: deptId,
        isActive: Boolean(isActive),
        oldDesigName: oldDesigName, // required for DailyAttendance rename on update
      };

      if (editingId) {
        const res = await axios.put(`${BASE_URL}/api/designation-master/${editingId}`, payload, {
          params: { finYearId: Number(finYearId) },
        });

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Successfully Updated");
          resetForm();
          setPage(1);
          await fetchList(1);
        } else {
          toast.error(res?.data?.message || "Update failed");
        }
      } else {
        const res = await axios.post(`${BASE_URL}/api/designation-master`, payload, {
          params: { finYearId: Number(finYearId) },
        });

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Successfully Inserted");
          resetForm();
          setPage(1);
          await fetchList(1);
        } else {
          toast.error(res?.data?.message || "Already Exists");
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => r.id));
    setSelectAll(true);
  };

  const handleDeleteSelected = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.post(
        `${BASE_URL}/api/designation-master/delete-many`,
        { ids: selectedIds },
        { params: { finYearId: Number(finYearId) } }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Removed Successfully");
        resetForm();
      } else {
        toast.error(res?.data?.message || "Cant Delete.It has referred in transaction");
      }

      const nextPage = page > 1 && rows.length === selectedIds.length ? page - 1 : page;
      setPage(nextPage);
      await fetchList(nextPage);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const getPageItems = (current, total) => {
    // returns: [1, "ellipsis", 8, 9, 10, 11, 12, "ellipsis", 22]
    const delta = 2; // pages around current
    const range = [];
    const rangeWithDots = [];

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    // Always include first page
    range.push(1);

    // Pages around current
    for (let i = left; i <= right; i++) range.push(i);

    // Always include last page (if total > 1)
    if (total > 1) range.push(total);

    // Remove duplicates and sort
    const unique = [...new Set(range)].sort((a, b) => a - b);

    // Add ellipsis
    for (let i = 0; i < unique.length; i++) {
      const pageNum = unique[i];
      const prev = unique[i - 1];

      if (i > 0 && pageNum - prev > 1) {
        rangeWithDots.push("ellipsis");
      }
      rangeWithDots.push(pageNum);
    }

    return rangeWithDots;
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DESIGNATION MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <BadgeCheck className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Designation Management
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Add / Edit / Delete / Search / Pagination
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={() => setShowForm((v) => !v)} className={ghostBtn}>
                {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-lg font-extrabold">Search Criteria</div>

                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={handleClearFilters} className={ghostBtn} disabled={loading}>
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={() => fetchList(page)}
                    className={ghostBtn}
                    disabled={loading}
                  >
                    <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                  </button>

                  <button type="submit" className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div>
                <label className={labelBase}>Search (Code / Name)</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Type to search..."
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>
            </form>
          )}

          {/* ==================== ADD / EDIT FORM ==================== */}
          {showForm && (
            <form onSubmit={handleSave} className={cardBase + " p-6 mb-6"}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                <div className="text-lg font-extrabold">
                  {editingId ? `Edit Designation (ID: ${editingId})` : "New Designation"}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={resetForm} className={ghostBtn}>
                    <RefreshCcw className="w-4 h-4" />
                    Clear
                  </button>
                  <button type="submit" className={primaryBtn} disabled={loading}>
                    <Save className="w-4 h-4" />
                    {editingId ? "Update" : "Save"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>Designation Code</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={desigCode}
                      onChange={(e) => setDesigCode(e.target.value)}
                      placeholder="e.g. SE"
                      className={inputBase}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelBase}>
                    Designation Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={desigName}
                      onChange={(e) => setDesigName(e.target.value)}
                      placeholder="e.g. SUPERVISOR"
                      className={inputBase}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelBase}>
                    Department <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Building2 className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select
                      value={deptId}
                      onChange={(e) => setDeptId(e.target.value)}
                      className={inputBase}
                    >
                      <option value="Select">Select</option>
                      {departments.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelBase}>IsActive</label>
                  <button
                    type="button"
                    onClick={() => setIsActive((v) => !v)}
                    className={`mt-2 w-full px-4 py-3 rounded-2xl border font-bold flex items-center justify-center gap-2 transition ${
                      isDark ? "border-white/10 hover:bg-white/10" : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {isActive ? (
                      <>
                        <ToggleRight className="w-5 h-5 text-green-500" /> Active
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5 text-gray-400" /> Inactive
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Code</th>
                    <th className="px-4 py-4 text-center font-bold">Designation Name</th>
                    <th className="px-4 py-4 text-center font-bold">Edit</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                        title="Select All"
                      />
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 5 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {r.desigCode}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                          <span className="font-medium">{r.desigName}</span>
                          <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            DeptId: {r.deptId} · {Number(r.isActive) === 1 ? "Active" : "Inactive"}
                          </div>
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleEdit(r.id)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelect(r.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ==================== REMOVE BUTTON ==================== */}
            {rows.length > 0 && (
              <div className="p-4 border-t border-gray-200 dark:border-white/10">
                <button onClick={handleDeleteSelected} disabled={selectedIds.length === 0} className={dangerBtn}>
                  🗑 Delete Selected
                </button>
              </div>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {getPageItems(page, totalPages).map((item, idx) => {
                  if (item === "ellipsis") {
                    return (
                      <span
                        key={`dots-${idx}`}
                        className={`px-4 py-2 text-sm font-bold ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        …
                      </span>
                    );
                  }

                  const pno = item;
                  const active = page === pno;

                  return (
                    <button
                      key={pno}
                      onClick={() => setPage(pno)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        active
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {pno}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DesignationMaster;